import { ComponentRef, forwardRef } from "react";

import { ProviderWithLogo } from "../ProviderWithLogo";
import { Button, ButtonProps } from "../ui/button";
import { BasicTooltip } from "../ui/Tooltip";

const COMMON_HOVER_EFFECT = "hover:bg-gray-100 dark:hover:bg-gray-800";

interface ModelNameButtonProps extends ButtonProps {
  readOnly?: boolean;
  providerDisplay?: string;
  modelName?: string;
  tooltipContent?: string;
  provider?: string;
  label?: string;
  logo?: React.ReactNode;
  asButton?: boolean;
  functionName?: string;
  showTooltip?: boolean;
  labelClassName?: string;
  showIcon?: boolean;
}

export const ModelNameButton = forwardRef<ComponentRef<typeof Button>, ModelNameButtonProps>(
  (
    {
      provider,
      modelName,
      providerDisplay,
      tooltipContent,
      label,
      logo,
      asButton = true,
      labelClassName,
      functionName,
      showTooltip = true,
      showIcon = true,
      readOnly: _readOnly,
      ...props
    },
    ref,
  ) => {
    const displayLabel = label || modelName;

    const tooltipText = modelName ? `${providerDisplay}: ${modelName}` : providerDisplay;

    if (!asButton || functionName) {
      return (
        <BasicTooltip content={tooltipContent || tooltipText} shouldShow={showTooltip}>
          <div className="flex items-center gap-2 whitespace-nowrap">
            {showIcon && (
              <div className="flex items-center">
                <ProviderWithLogo hideName provider={provider || ""} className="!mr-0" />
              </div>
            )}
            <div className="flex min-w-0 flex-col leading-tight">
              <span className={`max-w-[250px] truncate text-base font-medium ${labelClassName}`}>
                {displayLabel}
              </span>
              {functionName ? <span className="text-sm text-gray-500">{functionName}</span> : null}
            </div>
          </div>
        </BasicTooltip>
      );
    }

    const hoverEffect = COMMON_HOVER_EFFECT;

    return (
      <BasicTooltip shouldShow={showTooltip} content={tooltipContent || tooltipText}>
        <Button
          ref={ref}
          variant="ghost"
          className={`${
            props.className || ""
          } group relative flex transition-all duration-200 ${hoverEffect}`}
          {...props}
        >
          <div className="flex flex-row items-center gap-1.5">
            <div className="relative">
              {!logo && showIcon ? (
                <div className="transform transition-transform duration-200 group-hover:scale-110 group-active:scale-90">
                  <ProviderWithLogo hideName provider={provider || ""} className="!mr-0" />
                </div>
              ) : (
                (logo ?? null)
              )}
            </div>
            <span className="flex items-center whitespace-nowrap">
              {displayLabel ? (
                <span className={`max-w-[150px] truncate text-sm font-medium ${labelClassName}`}>
                  {displayLabel}
                </span>
              ) : (
                <span className="text-gray-500">Select model...</span>
              )}
            </span>
          </div>
        </Button>
      </BasicTooltip>
    );
  },
);
